import { computed, Injectable } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { Route } from 'dataModels/route';
import { QuoteControllerSelectors } from 'sharedStores/quote-controller/quote-controller.module';
import { PersonalSelectors } from 'stores/personal/personal.module';
import { QuoteSelectors } from 'stores/quote/quote.module';
import { QuoteFlowType } from 'sharedEnums/quote-flow-type';
import { PAGE_ROUTES, SECTION_ROUTES, BASE_ROUTES } from '../../constants/routes.constants';

/**
 * Navigation service for the ID-first AB test variant flow.
 *
 * Centralises all hardcoded route decisions that span multiple pages in the variant:
 *   ID number → contact details → personal details → confirm-personal-details → car details
 *
 * Page components should call the appropriate method here instead of building Route objects
 * inline. This makes it easy to trace where variant-specific navigation decisions are made.
 */
@UntilDestroy()
@Injectable({
    providedIn: 'root',
})
export class IdNumberFirstNavigationService {
    private readonly quoteFlowType = this.store$.selectSignal(QuoteSelectors.selectQuoteFlowType);
    private readonly ssrAbTest = this.store$.selectSignal(
        QuoteControllerSelectors.selectSsrAbTestVariants,
    );
    private readonly leadNumber = this.store$.selectSignal(QuoteSelectors.selectLeadNumber);
    private readonly policyHolder = this.store$.selectSignal(PersonalSelectors.selectPolicyHolder);
    private readonly skippedVariantId = this.store$.selectSignal(
        QuoteSelectors.selectSkippedVariantId,
    );

    // ---------------------------------------------------------------------------
    // Route constants — defined once here so they are easy to update or trace
    // ---------------------------------------------------------------------------

    public readonly ID_NUMBER_ROUTE: Route = {
        baseRoute: BASE_ROUTES.POLICY,
        pageRoute: PAGE_ROUTES.ID_NUMBER,
        sectionRoute: SECTION_ROUTES.PERSONAL_DETAILS,
        goToStart: false,
    };

    public readonly CONFIRM_DETAILS_ROUTE: Route = {
        baseRoute: BASE_ROUTES.POLICY,
        pageRoute: PAGE_ROUTES.CONFIRM_DETAILS,
        sectionRoute: SECTION_ROUTES.PERSONAL_DETAILS,
        goToStart: false,
    };

    public readonly CAR_DETAILS_ROUTE: Route = {
        baseRoute: BASE_ROUTES.CAR_INSURANCE,
        pageRoute: PAGE_ROUTES.CAR_DETAILS,
        sectionRoute: SECTION_ROUTES.CAR_DETAILS,
        goToStart: false,
    };

    public readonly IS_REGULAR_DRIVER_ROUTE: Route = {
        baseRoute: BASE_ROUTES.CAR_INSURANCE,
        pageRoute: PAGE_ROUTES.IS_REGULAR_DRIVER,
        sectionRoute: SECTION_ROUTES.REGULAR_DRIVER,
        goToStart: false,
    };

    public readonly GENDER_ROUTE: Route = {
        baseRoute: BASE_ROUTES.POLICY,
        pageRoute: PAGE_ROUTES.GENDER,
        sectionRoute: SECTION_ROUTES.PERSONAL_DETAILS,
        goToStart: false,
    };

    public readonly HAS_TRACKING_DEVICE_ROUTE: Route = {
        baseRoute: BASE_ROUTES.CAR_INSURANCE,
        pageRoute: PAGE_ROUTES.HAS_TRACKING_DEVICE,
        sectionRoute: SECTION_ROUTES.CAR_DETAILS,
        goToStart: false,
    };

    public constructor(private readonly store$: Store) {}

    // ---------------------------------------------------------------------------
    // Back-navigation support
    // ---------------------------------------------------------------------------

    /**
     * One-shot flag set before `location.back()` fires from the confirm-personal-details
     * page. Consumed by `skipPageEffect` in id-number-page to prevent an automatic
     * forward-skip when the user arrives at the id-number page via browser back.
     *
     * Plain boolean (not a signal) so that reading/clearing it inside an Angular effect
     * does not cause the effect to re-run and accidentally fire the skip on a second pass.
     */
    private _suppressNextIdPageSkip = false;

    /** Returns true (and clears the flag) if the next id-page visit should NOT auto-skip. */
    public consumeSuppressNextIdPageSkip(): boolean {
        const value = this._suppressNextIdPageSkip;
        this._suppressNextIdPageSkip = false;
        return value;
    }

    /** Called by the quote controller just before `location.back()` from confirm-details. */
    public setSuppressNextIdPageSkip(): void {
        this._suppressNextIdPageSkip = true;
    }

    /**
     * One-shot flag set by the quote controller when the user presses Back from
     * is-regular-driver. Consumed by getConfirmPersonalDetailsNextRoute so that
     * confirm-details routes forward to is-regular-driver (not back to car-details)
     * when the user re-confirms after reviewing via the back button.
     */
    private _isReturningFromRegularDriver = false;

    /** Called by the quote controller just before navigating to confirm-details from is-regular-driver back. */
    public setReturningFromRegularDriver(): void {
        this._isReturningFromRegularDriver = true;
    }

    /** Returns true (and clears the flag) when confirm-details should forward to is-regular-driver. */
    public consumeReturningFromRegularDriver(): boolean {
        const value = this._isReturningFromRegularDriver;
        this._isReturningFromRegularDriver = false;
        return value;
    }

    /**
     * One-shot flag set in onNextClick() BEFORE isSubmitting is toggled, to mark that
     * this ID submission originates from the variant entry page (car-insurance/"").
     *
     * The API interceptor creates a lead automatically on any API call for pages with
     * createLead:true in the CMS. By the time the prefill response arrives, leadNumber
     * is already truthy, making the old `!leadNumber()` check unreliable. This flag
     * is captured at the exact moment when isIdFirstVariant() is still definitively
     * correct (no lead, not yet submitting).
     */
    private _isVariantEntrySubmit = false;

    /** Called by id-number-page.onNextClick() when isIdFirstVariant() is true (variant entry page). */
    public setVariantEntrySubmit(): void {
        this._isVariantEntrySubmit = true;
    }

    /** Returns true (and clears the flag) to confirm this is a variant-entry prefill submission. */
    public consumeVariantEntrySubmit(): boolean {
        const value = this._isVariantEntrySubmit;
        this._isVariantEntrySubmit = false;
        return value;
    }

    // ---------------------------------------------------------------------------
    // Shared state signals
    // ---------------------------------------------------------------------------

    /** True when the idNumberFirst AB test variant is active and the flow is Vehicle. */
    public readonly isIdFirstVariant = computed(() => {
        if (this.quoteFlowType() !== QuoteFlowType.Vehicle) return false;
        return this.ssrAbTest()?.abTestVariants?.['idNumberFirst'] ?? false;
    });

    /** True when the policy holder already has an SA-ID or passport stored. */
    public readonly hasIdNumber = computed(
        () => !!this.policyHolder()?.person?.identification?.idNumber,
    );

    /**
     * True when all fields required by confirm-personal-details are present.
     * Used to decide whether personal details can be skipped/confirmed without
     * stepping through the individual personal-details pages.
     */
    public readonly hasAllPersonalDetails = computed(() => {
        const person = this.policyHolder()?.person;
        return !!(
            person?.gender?.code &&
            person?.firstName &&
            person?.lastName &&
            person?.phone &&
            person?.maritalStatus?.description &&
            person?.employmentStatus?.description &&
            person?.emailAddress
        );
    });

    /**
     * True when the user originally skipped the variant ID-entry page, meaning they went
     * through the full car-details flow first. This is the reliable signal that car questions
     * are already complete — checked on confirm-details to route to regular-driver instead
     * of restarting car-details.
     */
    public readonly hasCompletedCarQuestions = computed(() => this.skippedVariantId());

    // ---------------------------------------------------------------------------
    // Route resolution methods
    // ---------------------------------------------------------------------------

    /**
     * Determines the next route after the user confirms their personal details
     * (confirm-personal-details page).
     *
     * - Variant + no ID  → ID number page (user skipped ID entry, must enter ID now)
     * - Variant + has ID + car questions already done (skip-then-fill path)
     *                    → regular driver (continue from where car questions left off)
     * - Variant + has ID + car questions NOT done (enter-ID-first path)
     *                    → car details (start car questions)
     * - Vehicle flow (non-variant) → regular driver
     * - Other flows → fallbackRoute (CMS-configured button route)
     */
    public getConfirmPersonalDetailsNextRoute(fallbackRoute: Route): Route {
        if (this.isIdFirstVariant()) {
            // If the user pressed Back from is-regular-driver and landed here, route them
            // forward to is-regular-driver again — do NOT restart car-details.
            if (this.consumeReturningFromRegularDriver()) return this.IS_REGULAR_DRIVER_ROUTE;

            // If car questions are already done (path 2: skip-then-fill), go to regular-driver.
            if (this.hasCompletedCarQuestions()) return this.IS_REGULAR_DRIVER_ROUTE;

            // First-time confirm (path 1.a.b / 1.b.b): car questions still ahead → start them.
            return this.CAR_DETAILS_ROUTE;
        }
        if (this.quoteFlowType() === QuoteFlowType.Vehicle) {
            return this.IS_REGULAR_DRIVER_ROUTE;
        }
        return fallbackRoute;
    }

    /**
     * Determines the next route after the user selects a tracking device option
     * (car-has-tracking-device page).
     *
     * - Existing policy holder → regular driver (personal details already on file)
     * - Non-variant flow → ID number page (enter ID before personal details)
     * - Variant + no ID → ID number page (user skipped ID entry, must enter now)
     * - Variant + all details complete → regular driver (confirm-personal-details already done)
     * - Variant + ID present, details incomplete → gender (start personal-details flow)
     */
    public getTrackingDeviceNextRoute(policyHolderExists: boolean): Route {
        if (policyHolderExists) {
            return this.IS_REGULAR_DRIVER_ROUTE;
        }
        if (!this.isIdFirstVariant() || !this.hasIdNumber()) {
            return this.ID_NUMBER_ROUTE;
        }
        return this.hasAllPersonalDetails() ? this.IS_REGULAR_DRIVER_ROUTE : this.GENDER_ROUTE;
    }

    /**
     * Determines the next route after the user submits their contact details.
     * Returns null to signal that the component should use the CMS-configured route.
     *
     * - Variant + gender already prefilled → confirm-personal-details (review before car questions)
     * - Otherwise → null (caller should fall back to CMS btnProceed route)
     */
    public getContactDetailsNextRoute(): Route | null {
        if (this.isIdFirstVariant() && !!this.policyHolder()?.person?.gender?.code) {
            return this.CONFIRM_DETAILS_ROUTE;
        }
        return null;
    }

    /**
     * Determines the next route after a prefill API response on the ID number page.
     * CMS form is passed in so that CMS-configured button routes are used as fallbacks.
     *
     * Variant — first visit (no lead yet):
     *   - Prefilled (full or partial) → confirm-personal-details (missing fields shown as editable)
     *   - Not prefilled → contact-details (CMS btnNext route)
     *
     * Non-variant / revisit (lead already exists):
     *   - Fully prefilled → confirm-personal-details (CMS btnNext route, configured to confirm-details)
     *   - Not fully prefilled → gender or passport-gender route (citizen / non-citizen CMS routes)
     */
    public getIdNumberPrefillNextRoute(form: any, isPrefilled: boolean, isCitizen: boolean): Route {
        const isVariant = this.ssrAbTest()?.abTestVariants?.['idNumberFirst'] ?? false;

        // Use the one-shot flag set in onNextClick() to detect the variant entry page.
        // The old `!this.leadNumber()` check was unreliable: the API interceptor creates
        // a lead (due to createLead:true in CMS) BEFORE the prefill response arrives,
        // so leadNumber is already truthy by the time this method runs.
        if (isVariant && this.consumeVariantEntrySubmit()) {
            if (isPrefilled) {
                return this.CONFIRM_DETAILS_ROUTE;
            }
            return form?.btnNext?.details?.route;
        }

        if (isPrefilled && this.hasAllPersonalDetails()) {
            // User has a prefilled profile. Always route to confirm-details for review,
            // regardless of whether car questions were already done. The forward routing
            // from confirm-details (via getConfirmPersonalDetailsNextRoute) will decide
            // whether to go to car-details or is-regular-driver.
            return this.CONFIRM_DETAILS_ROUTE;
        }

        return isCitizen
            ? (form?.btnCitizen?.details?.route ?? form?.btnNext?.details?.route)
            : (form?.btnNotCitizen?.details?.route ?? form?.btnNext?.details?.route);
    }

    // ---------------------------------------------------------------------------
    // Back navigation support (uses CMS page sequence from quote controller state)
    // ---------------------------------------------------------------------------

    /**
     * Determines if back navigation should be overridden and returns the target route,
     * or null if normal browser back should be used.
     *
     * Override cases:
     * - Back from id-number when hasIdNumber OR hasCompletedCarQuestions → navigate to has-tracking-device
     *   (breaks the confirm-details loop)
     * - Back from is-regular-driver → navigate to confirm-details with returning flag
     *   (ensures correct forward routing from confirm-details)
     * - Back from confirm-details → navigate to id-number with suppress flag
     *   (ensures user sees id-number form, not auto-skip to gender)
     *
     * @param currentPageRoute - The current page route
     * @param currentSectionRoute - The current section route
     * @returns Target Route if override needed, null to use normal browser back
     */
    public getBackNavigationOverride(
        currentPageRoute: string,
        currentSectionRoute: string,
    ): Route | null {
        if (!this.isIdFirstVariant()) return null;

        // Override 1: Back from is-regular-driver → confirm-details
        if (
            currentPageRoute === PAGE_ROUTES.IS_REGULAR_DRIVER &&
            currentSectionRoute === SECTION_ROUTES.REGULAR_DRIVER
        ) {
            this.setReturningFromRegularDriver();
            return this.CONFIRM_DETAILS_ROUTE;
        }

        // Override 2: Back from confirm-details → id-number
        if (
            currentPageRoute === PAGE_ROUTES.CONFIRM_DETAILS &&
            currentSectionRoute === SECTION_ROUTES.PERSONAL_DETAILS
        ) {
            this.setSuppressNextIdPageSkip();
            return this.ID_NUMBER_ROUTE;
        }

        // Override 3: Back from id-number when user has ID or completed car questions
        // This exits the confirm-details loop
        if (
            currentPageRoute === PAGE_ROUTES.ID_NUMBER &&
            currentSectionRoute === SECTION_ROUTES.PERSONAL_DETAILS &&
            (this.hasCompletedCarQuestions() || this.hasIdNumber())
        ) {
            return this.HAS_TRACKING_DEVICE_ROUTE;
        }

        return null;
    }

    /**
     * Suppress id-number auto-skip for any personal-details page (except id-number itself).
     * Called before location.back() so that if the user eventually lands on id-number,
     * they see the form instead of auto-forwarding to the next page.
     */
    public suppressIdPageSkipForBackNavigation(
        currentPageRoute: string,
        currentSectionRoute: string,
    ): void {
        if (
            currentSectionRoute === SECTION_ROUTES.PERSONAL_DETAILS &&
            currentPageRoute !== PAGE_ROUTES.ID_NUMBER
        ) {
            this.setSuppressNextIdPageSkip();
        }
    }
}
